import axiosInstance from '../axiosInstance';

const BASE = '/reports';

// Strip empty params so the query string stays clean.
const cleanParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = v;
  });
  return out;
};

/**
 * Flight Report (read-only).
 * params: { dateFrom, dateTo, bookingStatus, productStatus, airline, search }
 * → { success, data: { summary, rows } }
 */
export const getFlightReport = (params = {}) =>
  axiosInstance
    .get(`${BASE}/flights`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Flight Report — DEPARTURE-GROUPED passenger manifest (SDM-RPT-007).
 * params: { dateFrom, dateTo, airline, departureAirport, arrivalAirport, supplier, bookingStatus }
 * → { success, data: { summary, flights: [{ ...flight, passengers: [] }] } }
 */
export const getFlightManifest = (params = {}) =>
  axiosInstance
    .get(`${BASE}/flights/manifest`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Distinct airlines / airports / suppliers for the flight-report criteria
 * dropdowns. → { success, data: { airlines, airports, suppliers } }
 */
export const getFlightFilterOptions = () =>
  axiosInstance.get(`${BASE}/flights/filter-options`).then((res) => res.data);

/**
 * Flight Report PDF export (blob). Same filters as the manifest.
 */
export const downloadFlightReportPdf = (params = {}) =>
  axiosInstance
    .get(`${BASE}/flights/pdf`, { params: cleanParams(params), responseType: 'blob' })
    .then((res) => res.data);

/**
 * Room Report (read-only).
 * params: { dateFrom, dateTo, bookingStatus, productStatus, destination, search }
 * → { success, data: { summary, rows } }
 */
export const getRoomReport = (params = {}) =>
  axiosInstance
    .get(`${BASE}/rooms`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Hotel Report — HOTEL-GROUPED rooming list (Accommodation Report).
 * params: { dateFrom, dateTo, hotel, country, supplier, bookingStatus }
 * → { success, data: { summary, hotels: [{ ...hotel, roomingList: [] }] } }
 */
export const getHotelManifest = (params = {}) =>
  axiosInstance
    .get(`${BASE}/hotels/manifest`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Distinct hotels / countries / suppliers for the hotel-report criteria
 * dropdowns. → { success, data: { hotels, countries, suppliers } }
 */
export const getHotelFilterOptions = () =>
  axiosInstance.get(`${BASE}/hotels/filter-options`).then((res) => res.data);

/**
 * Hotel Report PDF export (blob). Same filters as the manifest.
 */
export const downloadHotelReportPdf = (params = {}) =>
  axiosInstance
    .get(`${BASE}/hotels/pdf`, { params: cleanParams(params), responseType: 'blob' })
    .then((res) => res.data);

/**
 * Transfer Report (read-only).
 * params: { dateFrom, dateTo, bookingStatus, productStatus, moment, search }
 * → { success, data: { summary, rows } }
 */
export const getTransferReport = (params = {}) =>
  axiosInstance
    .get(`${BASE}/transfers`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Transfer Report — GROUPED transfer manifest (SDM-RPT-009).
 * params: { dateFrom, dateTo, transferType, supplier, country, pickupLocation,
 *           dropoffLocation, bookingStatus }
 * → { success, data: { summary, transfers: [{ ...transfer, bookings: [] }] } }
 */
export const getTransferManifest = (params = {}) =>
  axiosInstance
    .get(`${BASE}/transfers/manifest`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Distinct suppliers / countries / locations for the transfer-report criteria
 * dropdowns. → { success, data: { suppliers, countries, pickupLocations, dropoffLocations } }
 */
export const getTransferFilterOptions = () =>
  axiosInstance.get(`${BASE}/transfers/filter-options`).then((res) => res.data);

/**
 * Transfer Report PDF export (blob). Same filters as the manifest.
 */
export const downloadTransferReportPdf = (params = {}) =>
  axiosInstance
    .get(`${BASE}/transfers/pdf`, { params: cleanParams(params), responseType: 'blob' })
    .then((res) => res.data);

/**
 * Invoice Report (read-only).
 * params: { dateFrom, dateTo, docType, vatType, status, search }
 * → { success, data: { summary, rows } }
 */
export const getInvoiceReport = (params = {}) =>
  axiosInstance
    .get(`${BASE}/invoices`, { params: cleanParams(params) })
    .then((res) => res.data);

/**
 * Insurance Report (read-only).
 * params: { dateFrom, dateTo, company, bookingStatus, search }
 * → { success, data: { summary, rows, companies } }
 */
export const getInsuranceReport = (params = {}) =>
  axiosInstance
    .get(`${BASE}/insurance`, { params: cleanParams(params) })
    .then((res) => res.data);
